import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, Select, message, Card } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../components/DataTable';
import {
  EquipmentData,
  ResourceType,
  ResourceUnit,
  EQUIPMENT_TYPES,
  RESOURCE_TYPE_LABEL_KEYS,
  RESOURCE_UNIT_LABEL_KEYS
} from '../models/Resource';
import {
  getAllEquipments,
  createEquipment,
  updateEquipment,
  deleteEquipment
} from '../services/supplierService';
import { getApiErrorMessage } from '../utils/errorMessages';
import { useTranslation } from 'react-i18next';
const { Option } = Select;

const Equipment: React.FC = () => {
  const [data, setData] = useState<EquipmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<EquipmentData | null>(null);
  const [form] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAllEquipments();
      const resourceArray = response.execution_resources || response;
      const formattedData = resourceArray
        .filter((item: any) => EQUIPMENT_TYPES.includes(item.type))
        .map((item: any) => {
          const pricing = item.pricing_options?.[0];
          return {
            id: item.id,
            id_pricing: pricing?.id || '',
            name: item.name,
            brand: item.metadata?.brand || '',
            model: item.metadata?.model || '',
            serial_number: item.metadata?.serial_number || '',
            power: item.metadata?.power || '',
            purchase_date: item.metadata?.purchase_date || '',
            acquisition_value: item.metadata?.acquisition_value || '',
            unit: pricing?.unit || '',
            unit_price: pricing ? pricing.unit_price.toString() : '',
            description: item.description || '',
            type: item.type
          };
        });
      setData(formattedData);
    } catch (error: any) {
      message.error(t('loadError'));
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<EquipmentData> = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('typeLabel'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) =>
        type && RESOURCE_TYPE_LABEL_KEYS[type] ? t(RESOURCE_TYPE_LABEL_KEYS[type]) : type || '-',
    },
    {
      title: t('brand'),
      dataIndex: 'brand',
      key: 'brand',
    },
    {
      title: t('model'),
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: t('serialNumber'),
      dataIndex: 'serial_number',
      key: 'serial_number',
    },
    {
      title: t('power'),
      dataIndex: 'power',
      key: 'power',
    },
    {
      title: t('unit'),
      dataIndex: 'unit',
      key: 'unit',
      render: (unitValue: string) =>
        unitValue ? t(RESOURCE_UNIT_LABEL_KEYS[unitValue] || unitValue) : '-',
    },
    {
      title: t('unitPrice'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (price: string) => (price ? `${Number(price).toFixed(2)} €` : '-'),
    },
  ];

  const handleCreate = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: EquipmentData) => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteEquipment(id);
      message.success(t('deleteSuccess'));
      fetchData();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'deleteError', t));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingItem) {
        const updatePayload = {
          name: values.name,
          description: values.description,
          pricing: {
            pricing_id: editingItem.id_pricing,
            current_unit: editingItem.unit,
            unit: values.unit,
            unit_price: values.unit_price
          },
          metadata: {
            brand: values.brand,
            model: values.model,
            serial_number: values.serial_number,
            power: values.power,
            purchase_date: values.purchase_date,
            acquisition_value: values.acquisition_value
          }
        };
        await updateEquipment(editingItem.id!, updatePayload);
        message.success(t('updateSuccess'));
      } else {
        await createEquipment(values, values.type || ResourceType.VEHICLE);
        message.success(t('createSuccess'));
      }
      setModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'saveError', t));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0, marginBottom: 16 }}>
        {t('equipmentManagement')}
      </h1>
      <Card
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('add')}
            </Button>
          }
        >
          <DataTable
            data={data}
            columns={columns}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            searchPlaceholder={t('search')}
          />
        </Card>

        <Modal
          title={editingItem ? t('editEquipment') : t('addEquipment')}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={800}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ type: ResourceType.VEHICLE }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="name"
                label={t('name')}
                rules={[{ required: true, message: t('nameRequired') }]}
              >
                <Input placeholder={t('name')} />
              </Form.Item>

              <Form.Item
                name="type"
                label={t('typeLabel')}
                rules={[{ required: true, message: t('typeRequired') }]}
              >
                <Select>
                  {EQUIPMENT_TYPES.map((type) => (
                    <Option key={type} value={type}>
                      {t(RESOURCE_TYPE_LABEL_KEYS[type])}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="brand"
                label={t('brand')}
              >
                <Input placeholder={t('brand')} />
              </Form.Item>

              <Form.Item
                name="model"
                label={t('model')}
              >
                <Input placeholder={t('model')} />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="serial_number"
                label={t('serialNumber')}
              >
                <Input placeholder={t('serialNumber')} />
              </Form.Item>

              <Form.Item
                name="power"
                label={t('power')}
              >
                <Input placeholder={t('powerPlaceholder')} />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="purchase_date"
                label={t('purchaseDate')}
                rules={[
                  { pattern: /^\d{2}-\d{2}-\d{4}$/, message: t('dateFormat') }
                ]}
              >
                <Input placeholder="DD-MM-YYYY" />
              </Form.Item>

              <Form.Item
                name="acquisition_value"
                label={t('acquisitionValue')}
                rules={[
                  { pattern: /^\d+([.,]\d{1,2})?$/, message: t('invalidFormat') }
                ]}
              >
                <Input placeholder={t('acquisitionValue')} />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="unit"
                label={t('unit')}
                rules={[{ required: true, message: t('unitRequired') }]}
              >
                <Select placeholder={t('selectUnit')}>
                  {Object.values(ResourceUnit).map((value) => (
                    <Option key={value} value={value}>
                      {t(RESOURCE_UNIT_LABEL_KEYS[value])}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="unit_price"
                label={t('unitPrice')}
                rules={[
                  { required: true, message: t('unitPriceRequired') },
                  { pattern: /^\d+([.,]\d{1,2})?$/, message: t('invalidFormat') }
                ]}
              >
                <Input placeholder={t('unitPrice')} />
              </Form.Item>
            </div>

            <Form.Item
              name="description"
              label={t('notes')}
            >
              <Input.TextArea rows={3} placeholder={t('additionalNotes')} />
            </Form.Item>
          </Form>
        </Modal>
    </div>
  );
};

export default Equipment;
